import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';

export default function TenantsPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const { data = [] } = useQuery({ queryKey: ['tenants'], queryFn: async () => (await api.get('/tenants')).data });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold">Tenants</h1><p className="text-sm text-slate-500">All organizations using SupportByte.</p></div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New tenant</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Plan</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Tickets</th><th className="text-left px-4 py-3">Agents</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data as { id: string; name: string; slug: string; planTier: string; status: string; isInternal: boolean; _count: { tickets: number; agents: number } }[]).map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-3 font-medium">{t.name}{t.isInternal && <span className="badge bg-brand-100 text-brand-700 ml-2">internal</span>}</td>
                <td className="px-4 py-3 font-mono text-xs">{t.slug}</td>
                <td className="px-4 py-3"><span className="badge bg-slate-100 text-slate-700">{t.planTier}</span></td>
                <td className="px-4 py-3"><span className="badge bg-emerald-100 text-emerald-700">{t.status}</span></td>
                <td className="px-4 py-3">{t._count.tickets}</td>
                <td className="px-4 py-3">{t._count.agents}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <NewTenantModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ['tenants'] }); }} />}
    </div>
  );
}

function NewTenantModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  // No default password — every tenant owner used to be created with the same
  // well-known 'owner@123' unless the admin remembered to overwrite it.
  const [ownerPassword, setOwnerPassword] = useState('');
  const create = useMutation({
    mutationFn: async () => api.post('/tenants', { slug, name, email, ownerName, ownerPassword }),
    onSuccess: () => { toast.success('Tenant created'); onCreated(); },
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center p-4 z-50">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">New tenant</h2>
        <div className="space-y-3">
          <div><label className="label">Slug</label><input className="input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme" /></div>
          <div><label className="label">Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><label className="label">Owner email</label><input className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><label className="label">Owner name</label><input className="input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} /></div>
          <div><label className="label">Owner password</label><input type="password" className="input" value={ownerPassword} onChange={(e) => setOwnerPassword(e.target.value)} autoComplete="new-password" /></div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!slug || !name || !email || !ownerPassword || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
